import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { MeetingListItem, MeetingListResponse, MeetingSortKey, ProjectListResponse } from '../../shared/api-types.js';
import { isOneOf } from '../../shared/enums.js';
import { api, queryString } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import {
  Button,
  Chip,
  DataTable,
  Dialog,
  EM_DASH,
  Icon,
  IconButton,
  LinkButton,
  Page,
  PageHead,
  ProjectChip,
  RelativeTime,
  SelectField,
  StatusPill,
  TextField,
  browserTimeZone,
  formatCount,
  formatDate,
  formatDateTime,
  formatDuration,
  formatTime,
  pluralise,
  timeZoneLabel,
  useIsMobile,
  useRequest,
  useUrlSort,
  type DataTableColumn,
  type SortParamOptions,
  type SortState,
} from '../ui/index.js';
import { summaryPresentation } from './lib/summary.js';
import {
  PROJECT_NONE_CHIP_LABEL,
  PROJECT_NONE_PARAM_VALUE,
  PROJECT_UNKNOWN_CHIP_LABEL,
  projectFilterEchoAction,
  projectFilterProject,
  type ProjectFilterOption,
} from './lib/todos.js';
import { OWN_PROJECTS_PATH } from './project-checkboxes.js';
import { ProjectChipList } from './project-filing.js';
import { ProjectFilterField } from './project-filter.js';
import './pages.css';

// Every meeting imported from Fellow (spec §13): a table that sorts by every column with the sort
// in the URL, a title search, two structured filters — "Summary: Not processed", the meetings imported
// outside the automatic window that wait for the person (spec §10), and, under the projects floor,
// "Project" (ADR 0017) — and an empty state that explains the import is automatic. Each filter is named
// after the column it narrows (04-data §1.5: a structured filter names a column, an operator and a value)
// and lives in the URL (`?processed=false`, `?project=<id>`) so the overview and a project's page can link to it.

const DEFAULT_SORT: SortState = { key: 'started_at', dir: 'desc' };
/** Sort keys the API orders the whole set by; anything else is sorted on the loaded rows. */
export const MEETING_SERVER_SORT_KEYS: readonly MeetingSortKey[] = ['started_at', 'title', 'todo_count', 'project'];
const COLUMN_KEYS = ['title', 'started_at', 'project', 'attendee_count', 'summary_status', 'todo_count', 'last_synced_at'] as const;
const SEARCH_DEBOUNCE_MS = 300;
/** The URL param (and API query) for the processing filter; only this value is meaningful. */
const PROCESSED_PARAM = 'processed';
/** The URL param (and API query) for the project filter: a project's id, or `none` (ADR 0017). */
const PROJECT_PARAM = 'project';
const PROCESSING_OPTIONS = [
  { value: 'all', label: 'Any' },
  { value: 'unprocessed', label: 'Not processed' },
] as const;
type ProcessingFilter = (typeof PROCESSING_OPTIONS)[number]['value'];

interface SheetDraft {
  processing: ProcessingFilter;
  project: string | null;
}

/** The quick search names what it reaches (04-data §1.5): project names only where the floor lets `q` match them. */
export function meetingsSearchLabel(projectsOn: boolean): string {
  return projectsOn ? 'Search meeting titles and projects' : 'Search meeting titles';
}

export function meetingsPath(q: string, sort: SortState, unprocessed: boolean, project: string | null = null): string {
  // Why: the API orders the whole set by the keys it knows; the table re-sorts the loaded rows for
  // the rest, so every column sorts and a server-sorted column is right even past the row cap.
  const serverSort = isOneOf(MEETING_SERVER_SORT_KEYS, sort.key) ? sort : null;
  return `/api/meetings${queryString({ q: q || null, [PROCESSED_PARAM]: unprocessed ? 'false' : null, [PROJECT_PARAM]: project, sort: serverSort?.key ?? null, dir: serverSort?.dir ?? null })}`;
}

function durationSeconds(meeting: Pick<MeetingListItem, 'startedAt' | 'endedAt'>): number | null {
  if (!meeting.startedAt || !meeting.endedAt) return null;
  const seconds = (new Date(meeting.endedAt).getTime() - new Date(meeting.startedAt).getTime()) / 1000;
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

export default function MeetingsPage() {
  const me = useMe();
  const isMobile = useIsMobile();
  // The project column, filter and chip exist only under the projects floor (owner ruling 9 of 2026-10-02; selection
  // only — the API ignores the parameter with the floor off and sends no projects on a row).
  const projectsOn = me.integrations.projects === 'on';
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const unprocessed = params.get(PROCESSED_PARAM) === 'false';
  const processing: ProcessingFilter = unprocessed ? 'unprocessed' : 'all';
  const rawProject = params.get(PROJECT_PARAM);
  const project = projectsOn && rawProject && rawProject.trim() !== '' ? rawProject.trim() : null;
  const [draft, setDraft] = useState(q);
  const sortOptions = useMemo<SortParamOptions<string>>(() => ({ keys: COLUMN_KEYS.filter((key) => projectsOn || key !== 'project'), defaultSort: DEFAULT_SORT }), [projectsOn]);
  const [sort] = useUrlSort(sortOptions);
  const path = meetingsPath(q, sort, unprocessed, project);
  const request = useRequest<MeetingListResponse>((signal) => api.get<MeetingListResponse>(path, { signal }), [path]);
  const qRef = useRef(q);
  qRef.current = q;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDraft, setSheetDraft] = useState<SheetDraft>({ processing, project });

  // The reader's own live projects for the Project filter; a failure here is said beside the select (D18), and a chip still
  // names its project from the list's own answer.
  const projects = useRequest<ProjectListResponse | null>(
    (signal) => (projectsOn ? api.get<ProjectListResponse>(OWN_PROJECTS_PATH, { signal }) : Promise.resolve({ ok: true as const, status: 200, data: null })),
    [projectsOn],
  );
  const projectOptions: ProjectFilterOption[] | null = useMemo(
    () => (projects.data ? projects.data.items.map((item) => ({ id: item.id, name: item.name, slot: item.slot })) : null),
    [projects.data],
  );

  useEffect(() => {
    setDraft(q);
  }, [q]);

  const setQuery = useCallback((next: string) => {
    setParams((current) => {
      const copy = new URLSearchParams(current);
      if (next.trim() === '') copy.delete('q');
      else copy.set('q', next.trim());
      return copy;
    }, { replace: true });
  }, [setParams]);

  useEffect(() => {
    if (draft.trim() === qRef.current) return;
    const handle = window.setTimeout(() => setQuery(draft), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft, setQuery]);

  const setProcessing = useCallback((next: ProcessingFilter) => {
    setParams((current) => {
      const copy = new URLSearchParams(current);
      if (next === 'unprocessed') copy.set(PROCESSED_PARAM, 'false');
      else copy.delete(PROCESSED_PARAM);
      return copy;
    }, { replace: true });
  }, [setParams]);

  const setProject = useCallback((next: string | null) => {
    setParams((current) => {
      const copy = new URLSearchParams(current);
      if (next) copy.set(PROJECT_PARAM, next);
      else copy.delete(PROJECT_PARAM);
      return copy;
    }, { replace: true });
  }, [setParams]);

  const applySheet = useCallback((next: SheetDraft) => {
    setParams((current) => {
      const copy = new URLSearchParams(current);
      if (next.processing === 'unprocessed') copy.set(PROCESSED_PARAM, 'false');
      else copy.delete(PROCESSED_PARAM);
      if (next.project) copy.set(PROJECT_PARAM, next.project);
      else copy.delete(PROJECT_PARAM);
      return copy;
    }, { replace: true });
  }, [setParams]);

  // Clear-all restores rows, not the arrangement (04-data §1.5): the sort in the URL is left alone.
  const clearAll = useCallback(() => {
    setDraft('');
    setParams((current) => {
      const copy = new URLSearchParams(current);
      copy.delete('q');
      copy.delete(PROCESSED_PARAM);
      copy.delete(PROJECT_PARAM);
      return copy;
    }, { replace: true });
  }, [setParams]);
  const anyFilter = q !== '' || unprocessed || project !== null;
  const activeFilterCount = (q !== '' ? 1 : 0) + (unprocessed ? 1 : 0) + (project !== null ? 1 : 0);
  const structuredCount = (unprocessed ? 1 : 0) + (project !== null ? 1 : 0);

  // What the list's answer says of the project in the address (design §2.3, §7.7): an absorbed id is rewritten to its
  // survivor's, one that names nothing is dropped, and a status line says so — a bookmark never lands quietly on a
  // different set. The guards against an answer still on its way are projectFilterEchoAction's.
  const data = request.data;
  const echo = useMemo(() => (data ? { project: data.project, resolvedFrom: data.resolvedFrom } : null), [data]);
  const knownIds = useMemo(() => (projectOptions ? projectOptions.map((option) => option.id) : null), [projectOptions]);
  const settled = request.status === 'ready' && !request.refreshing;
  const [projectNotice, setProjectNotice] = useState<{ text: string; project: string | null } | null>(null);
  const projectRef = useRef(project);
  projectRef.current = project;
  useEffect(() => {
    if (!projectsOn) return;
    const action = projectFilterEchoAction(projectRef.current, echo, knownIds, settled);
    if (!action) return;
    const next = action.kind === 'rewrite' ? action.to.id : null;
    setProjectNotice({ text: action.notice, project: next });
    setProject(next);
  }, [echo, knownIds, settled, projectsOn, setProject]);
  useEffect(() => {
    setProjectNotice((current) => (current && current.project === project ? current : null));
  }, [project]);
  const filterProject = projectFilterProject(project, echo, projectOptions);

  const processingField = (value: ProcessingFilter, set: (next: ProcessingFilter) => void) => (
    <SelectField
      label="Summary"
      value={value}
      onChange={(event) => set(event.target.value as ProcessingFilter)}
      options={PROCESSING_OPTIONS}
      className="filter-bar__status"
    />
  );
  // The shared Project filter (project-filter.tsx): with its projects' failure said beside it, never "Loading projects…"
  // left standing over a failed load (Phase 1c D18).
  const projectField = (value: string | null, set: (next: string | null) => void) => (
    <ProjectFilterField value={value} onChange={set} options={projectOptions} failure={projects.status === 'error' ? projects.error : null} onRetry={projects.reload} />
  );
  const manageProjects = <LinkButton to={PATHS.projects} variant="link" size="sm" icon="folder">Manage projects</LinkButton>;

  // Why: a time is always shown with its zone (04-data §4) — in the head's one sentence (the mobile
  // cards carry no column heading), in the column heading once, and in full on each cell's title and
  // accessible name, as the audit log does.
  const timeZone = browserTimeZone();
  const zoneLabel = timeZoneLabel(timeZone);

  // Folding (04-data §1.7; ADR 0168): Last synced (scanning only) first, then the supplementary Attendees and Project,
  // then the values a meeting is compared by — its summary, its open to-dos, and When, the default sort, last. The
  // title names the row and never folds.
  const columns = useMemo<DataTableColumn<MeetingListItem>[]>(() => {
    const all: DataTableColumn<MeetingListItem>[] = [
      {
        key: 'title',
        header: 'Title',
        type: 'text',
        sortValue: (meeting) => meeting.title,
        card: 'title',
        cell: (meeting) => <Link to={PATHS.meeting(meeting.id)} className="hit-area">{meeting.title}</Link>,
      },
      {
        key: 'started_at',
        header: `When (${zoneLabel})`,
        name: 'When',
        type: 'date',
        sortValue: (meeting) => meeting.startedAt,
        card: 'meta',
        fold: 6,
        cell: (meeting) => {
          const seconds = durationSeconds(meeting);
          if (!meeting.startedAt) return <span className="muted" aria-label="Start time not recorded">{EM_DASH}</span>;
          const absolute = formatDateTime(meeting.startedAt, { timeZone });
          return (
            <span className="num" title={absolute}>
              <time dateTime={meeting.startedAt} aria-label={absolute}>{formatDate(meeting.startedAt, { timeZone })}, {formatTime(meeting.startedAt, { timeZone })}</time>
              {seconds !== null ? <span className="muted"> · {formatDuration(seconds)}</span> : null}
            </span>
          );
        },
      },
      {
        key: 'project',
        header: 'Project',
        type: 'text',
        // Why the first name: the server orders by it too (sort `project`, NULLs last), so the loaded rows and the whole
        // set agree; a meeting in no project sorts after the rest in either direction.
        sortValue: (meeting) => meeting.projects[0]?.name ?? null,
        card: 'meta',
        fold: 3,
        cell: (meeting) => <ProjectChipList projects={meeting.projects} className="meeting-projects" />,
      },
      {
        key: 'attendee_count',
        header: 'Attendees',
        type: 'number',
        numeric: true,
        sortValue: (meeting) => meeting.attendeeCount,
        card: 'field',
        fold: 2,
        cell: (meeting) => formatCount(meeting.attendeeCount),
      },
      {
        key: 'summary_status',
        header: 'Summary',
        type: 'enum',
        sortValue: (meeting) => summaryPresentation(meeting.summaryStatus, meeting.transcriptStatus, meeting.processedAt).rank,
        card: 'meta',
        fold: 4,
        cell: (meeting) => {
          const state = summaryPresentation(meeting.summaryStatus, meeting.transcriptStatus, meeting.processedAt);
          return <StatusPill tone={state.tone} icon={state.icon} title={state.detail}>{state.word}</StatusPill>;
        },
      },
      {
        key: 'todo_count',
        header: 'Open to-dos',
        type: 'number',
        numeric: true,
        sortValue: (meeting) => meeting.openTodoCount,
        card: 'field',
        fold: 5,
        cell: (meeting) => formatCount(meeting.openTodoCount),
      },
      {
        key: 'last_synced_at',
        header: 'Last synced',
        type: 'date',
        sortValue: (meeting) => meeting.lastSyncedAt,
        card: 'field',
        fold: 1,
        cell: (meeting) => <RelativeTime value={meeting.lastSyncedAt} />,
      },
    ];
    return projectsOn ? all : all.filter((column) => column.key !== 'project');
  }, [timeZone, zoneLabel, projectsOn]);

  const status = request.status === 'loading'
    ? { kind: 'loading' as const, rowCount: 6 }
    : request.status === 'error' && request.error
      ? { kind: 'error' as const, failure: request.error, onRetry: request.reload }
      : { kind: 'ready' as const };
  const truncated = data && data.items.length < data.matching;
  const searched = projectsOn ? 'Only titles and project names are searched.' : 'Only titles are searched.';

  return (
    <Page measure="wide">
      <PageHead title="Meetings" subtitle={`Times are shown in ${zoneLabel}.`} />
      <div className="filter-bar stack stack--3">
        <div className="filter-bar__controls">
          <div className="filter-bar__search">
            <Icon name="search" size={16} className="filter-bar__search-icon" />
            <TextField
              label={meetingsSearchLabel(projectsOn)}
              type="search"
              inputMode="search"
              autoComplete="off"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              className="filter-bar__search-field"
            />
            {draft !== '' ? <IconButton icon="x" label="Clear search" size="sm" className="filter-bar__search-clear" onClick={() => { setDraft(''); setQuery(''); }} /> : null}
          </div>
          {isMobile ? (
            // 03-components §10 mobile: the structured filters fold into a sheet with an explicit Apply;
            // the chips below stay outside it as the evidence of what is applied.
            <Button variant="secondary" icon="filter" onClick={() => { setSheetDraft({ processing, project }); setSheetOpen(true); }} aria-haspopup="dialog">
              {structuredCount > 0 ? `Filters (${structuredCount})` : 'Filters'}
            </Button>
          ) : (
            <>
              {processingField(processing, setProcessing)}
              {projectsOn ? projectField(project, setProject) : null}
              {projectsOn ? manageProjects : null}
            </>
          )}
        </div>
        {anyFilter || data ? (
          <div className="filter-bar__chips split">
            <ul className="cluster" role="list" aria-label="Active filters">
              {unprocessed ? (
                <li>
                  <Chip tone="accent" selected onRemove={() => setProcessing('all')} removeLabel="Remove filter: Summary: Not processed">Summary: Not processed</Chip>
                </li>
              ) : null}
              {project !== null ? (
                <li>
                  {project === PROJECT_NONE_PARAM_VALUE ? (
                    <Chip tone="accent" selected onRemove={() => setProject(null)} removeLabel={`Remove filter: ${PROJECT_NONE_CHIP_LABEL}`}>{PROJECT_NONE_CHIP_LABEL}</Chip>
                  ) : filterProject ? (
                    // The project's own label chip (design §7.6), so the word travels with its colour; named from the list's answer first.
                    <ProjectChip project={filterProject} prefix="Project: " link={false} onRemove={() => setProject(null)} removeLabel="Remove filter: project" />
                  ) : (
                    <Chip tone="accent" selected onRemove={() => setProject(null)} removeLabel="Remove filter: project">{PROJECT_UNKNOWN_CHIP_LABEL}</Chip>
                  )}
                </li>
              ) : null}
              {q !== '' ? (
                <li>
                  <Chip tone="accent" selected onRemove={() => setQuery('')} removeLabel={`Remove filter: search ${q}`}>Search: {q}</Chip>
                </li>
              ) : null}
              {activeFilterCount >= 2 ? (
                <li>
                  <Button variant="link" size="sm" onClick={clearAll}>Clear all</Button>
                </li>
              ) : null}
            </ul>
            {data ? (
              <p className="filter-bar__count text-sm secondary num" role="status" aria-live="polite">
                {anyFilter ? `${formatCount(data.matching)} of ${pluralise(data.total, { one: '# meeting', other: '# meetings' })}` : pluralise(data.total, { one: '# meeting', other: '# meetings' })}
              </p>
            ) : null}
          </div>
        ) : null}
        {/* One visible status line, announced once (01-foundations §7.3): what the address's project id turned out to be. */}
        {projectsOn && projectNotice && projectNotice.project === project ? (
          <p className="filter-bar__notice text-sm secondary" role="status" aria-live="polite">{projectNotice.text}</p>
        ) : null}
        <Dialog
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Filters"
          size="sm"
          initialFocus="first"
          actions={
            <>
              <Button variant="secondary" onClick={() => setSheetOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => { applySheet(sheetDraft); setSheetOpen(false); }}>Apply</Button>
            </>
          }
        >
          <div className="stack stack--3">
            {processingField(sheetDraft.processing, (next) => setSheetDraft((current) => ({ ...current, processing: next })))}
            {projectsOn ? projectField(sheetDraft.project, (next) => setSheetDraft((current) => ({ ...current, project: next }))) : null}
            {projectsOn ? <div>{manageProjects}</div> : null}
          </div>
        </Dialog>
      </div>
      <DataTable<MeetingListItem, string>
        caption="Meetings"
        failureNoun="your meetings"
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(meeting) => meeting.id}
        status={status}
        sort={{ defaultSort: DEFAULT_SORT }}
        clientSort
        empty={{
          title: 'No meetings yet',
          description: me.integrations.fellow === 'off'
            ? 'The Fellow integration is not switched on in this installation, so nothing can be imported yet.'
            : 'Meetings import from Fellow once your account is connected; the first sync usually takes a minute or two.',
          action: me.integrations.fellow === 'off' ? undefined : <LinkButton to={PATHS.connections} variant="primary" icon="plug">Open Connections</LinkButton>,
        }}
        filtered={{
          active: anyFilter,
          onClear: clearAll,
          title: unprocessed && q === '' && project === null ? 'Nothing is waiting to be processed' : q === '' ? 'No meetings match these filters' : 'No meetings match that search',
          description: unprocessed && q === '' && project === null
            ? 'Every meeting with a transcript has been processed.'
            : q === ''
              ? 'Try another project or summary state, or clear the filters to see every meeting.'
              : unprocessed || project !== null
                ? `${searched.replace('.', '')}, among the meetings these filters leave. Try another word, or clear the filters to see every meeting.`
                : `${searched} Try another word, or clear the search to see every meeting.`,
        }}
        mobile="cards"
        // Why only when rows were left out: the filter bar's count line already states the figure; the
        // footer's one job is position and total past the row cap (03-components §11).
        footer={data && truncated ? (
          <p className="text-sm secondary num">
            Showing the first {formatCount(data.items.length)} of {formatCount(data.matching)} matching meetings. Narrow the search to see the rest.
          </p>
        ) : null}
      />
    </Page>
  );
}
